import React, { useState, useEffect } from 'react';
import Tabs from '@components/tabs/tabs';
import Tab from '@components/tabs/tab';

import Typography from '@components/typography';

import { useDeviceDetect } from '@hooks/usedevicedetect';

import Accordion, {
  AccordionItemData,
  AccordionItemValue,
} from '@components/accordion/accordion';
import { v4 as uuid4 } from 'uuid';
import WhyZenAuto from '../whyzenauto/whyzenauto';
import { useSitcoreComponentData } from '@hooks/usesitecorecomponentdata';
import { useAppSelector, useAppDispatch } from '@hooks/statehooks';
import { updateVehicleTechnical } from '@store/reducers/vehicleinformation';
import { getVehicleTechnical } from '@services/index';
import Loader from '@components/loader/loader';

import TrustPilot from './trustpilot';
import { LeaseConfiguratorConfig, VehicleProps } from 'interface';
import Tooltip from '@components/tooltip';

import { evWLTPTooltip, technicalTooltipQueryText } from '../../../resources';

const Information: React.FC<VehicleProps> = ({ vehicle }) => {
  const tabName: string = 'vehicleInformation';
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const [details, setDetails] = useState<AccordionItemData[]>([]);
  const technicalData = useAppSelector(
    (state) => state.vehicleInformation.technicalSpec
  );
  const { sitecoreReady, config } = useSitcoreComponentData();
  const { isMobile, isTablet } = useDeviceDetect();
  const isMobileOrTablet = isTablet || isMobile;

  const selectedTab = useAppSelector((state) => state.tabs);
  const [technicalSpec, setTechnicalSpec] = useState<AccordionItemData[]>();

  useEffect(() => {
    const accordionDetailsData: AccordionItemData[] = [];
    if (vehicle.equipment) {
      Object.entries(vehicle.equipment).forEach(([key, value], index) => {
        accordionDetailsData.push({
          index: index,
          title: key,
          content: value,
        });
      });
    }

    setDetails(accordionDetailsData);
  }, [vehicle.equipment]);

  useEffect(() => {
    if (technicalData) {
      const accordionTechnicalSpecData: AccordionItemData[] = [];

      technicalData.groups.map((item, index) => {
        let accordionItems = item.items as unknown as AccordionItemValue[];
        let updatedArray: AccordionItemValue[] = accordionItems;
        updatedArray = accordionItems.map((itemLabel, indexLabel) => {
          if (
            itemLabel.label &&
            itemLabel.label
              .toLowerCase()
              .includes(technicalTooltipQueryText.toLowerCase())
          ) {
            itemLabel = {
              ...itemLabel,
              tooltip: (
                <Tooltip
                  key={uuid4()}
                  id={`vehicle-tech-tooltip-${indexLabel}`}
                  text=""
                  delayHide={100}
                  data-cy="vehicle-tech-tooltip"
                  data-testid="vehicle-tech-tooltip"
                >
                  <Typography variant="caption" size="xs" colour="alternative">
                    {evWLTPTooltip}
                  </Typography>
                </Tooltip>
              ),
            };
          }
          return itemLabel;
        });

        accordionTechnicalSpecData.push({
          index: index,
          title: item.label,
          content: updatedArray,
        });
      });

      setTechnicalSpec(accordionTechnicalSpecData);
    } else if (selectedTab[tabName] === 1) {
      setLoading(true);
      getVehicleTechnical(vehicle.id).subscribe({
        next: (result) => {
          dispatch(updateVehicleTechnical(result));
          setLoading(false);
        },
        error: () => {
          setLoading(false);
        },
      });
    }
  }, [selectedTab, technicalData]);

  return (
    <div className="main-panel">
      <div className="main-panel--container">
        <div className="information">
          <div className="information__header">
            <Typography variant="h2" size="x" weight="bold">
              Detailed Vehicle Information
            </Typography>
          </div>
          <div className="information__tabs">
            <Tabs name={tabName}>
              <Tab
                key={uuid4()}
                title={isMobile ? 'Std. Equipment' : 'Standard Equipment'}
              >
                <Accordion
                  data={details}
                  searchPlaceholder={'Search standard equipment'}
                  isSearchable={true}
                  type="normal"
                />
              </Tab>
              <Tab key={uuid4()} title="Technical Data">
                {loading ? (
                  <Loader />
                ) : (
                  <Accordion
                    data={technicalSpec as AccordionItemData[]}
                    searchPlaceholder={'Search technical data'}
                    isSearchable={true}
                    type="hasvalue"
                  />
                )}
              </Tab>
            </Tabs>
          </div>
        </div>
        {sitecoreReady && (
          <WhyZenAuto data={config as LeaseConfiguratorConfig} />
        )}
        {sitecoreReady && isMobileOrTablet && config.trustPilot && (
          <div className="trustpilot">
            <TrustPilot
              href={config.trustPilot.href}
              dataTemplateId={config.trustPilot.dataTemplateId}
              dataBusinessUnitId={config.trustPilot.dataBusinessUnitId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Information;
